/* Downloading drawings as the client expects them: one file per sheet.
 *
 * Sheets uploaded as a combined set all carry the SAME fileUrl (the whole
 * uploaded PDF) plus a page number — so a naive "download originals" handed
 * the client the combined package, once, instead of their sheets. This
 * fetches each source file a single time, splits the combined ones page by
 * page with pdf-lib (vectors preserved — a real PDF, not a screenshot), and
 * saves every sheet under its own drawing number. Single-sheet files download
 * untouched. */

export interface DownloadableSheet {
  code: string;
  title?: string;
  revision?: string;
  fileUrl?: string;
  fileName?: string;
  page?: number;               // 1-based page within a combined set
}

const save = (blob: Blob | string, name: string) => {
  const url = typeof blob === "string" ? blob : URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  if (typeof blob !== "string") setTimeout(() => URL.revokeObjectURL(url), 8000);
};

const sheetName = (s: DownloadableSheet) =>
  `${[s.code, s.revision].filter(Boolean).join("_")}${s.title ? "_" + s.title.slice(0, 60) : ""}`
    .replace(/[^\w\-. ]+/g, " ").trim().replace(/\s+/g, "_") + ".pdf";

/** Browsers throttle bursts of programmatic downloads — pace them out. */
const pause = (ms: number) => new Promise((r) => setTimeout(r, ms));

export async function downloadSheetFiles(
  sheets: DownloadableSheet[],
  onProgress?: (done: number, total: number) => void,
): Promise<{ saved: number; failed: number }> {
  const withFiles = sheets.filter((s) => s.fileUrl);
  const byUrl = new Map<string, DownloadableSheet[]>();
  for (const s of withFiles) {
    if (!byUrl.has(s.fileUrl!)) byUrl.set(s.fileUrl!, []);
    byUrl.get(s.fileUrl!)!.push(s);
  }
  const total = withFiles.length;
  let done = 0, saved = 0, failed = 0;
  const tick = () => onProgress?.(done, total);
  tick();

  for (const [url, group] of byUrl) {
    /* A file is "combined" when sheets address pages inside it. One sheet with
       no page number is its own original — download untouched, name intact. */
    const combined = group.length > 1 || group.some((s) => s.page != null);
    const isPdf = /\.pdf($|\?)/i.test(group[0].fileName ?? "") || !/\.(png|jpe?g|webp|dwg)($|\?)/i.test(group[0].fileName ?? "");
    if (!combined || !isPdf) {
      for (const s of group) {
        save(url, s.fileName || sheetName(s));
        done++; saved++; tick();
        await pause(700);
      }
      continue;
    }
    try {
      const [{ PDFDocument }, buf] = await Promise.all([
        import("pdf-lib"),
        fetch(url, { credentials: "same-origin" }).then((r) => { if (!r.ok) throw new Error(String(r.status)); return r.arrayBuffer(); }),
      ]);
      const src = await PDFDocument.load(buf, { ignoreEncryption: true });
      const pageCount = src.getPageCount();
      for (const s of group) {
        const idx = Math.min(Math.max(0, (s.page ?? 1) - 1), pageCount - 1);
        try {
          const out = await PDFDocument.create();
          const [pg] = await out.copyPages(src, [idx]);
          out.addPage(pg);
          const bytes = await out.save();
          const copy = new Uint8Array(bytes.length);
          copy.set(bytes);
          save(new Blob([copy.buffer], { type: "application/pdf" }), sheetName(s));
          saved++;
        } catch { failed++; }
        done++; tick();
        await pause(700);
      }
    } catch {
      /* The split failed (unreachable, malformed, encrypted) — the combined
         original is still better than nothing, once, said plainly by its name. */
      save(url, group[0].fileName || "drawing_set.pdf");
      done += group.length; failed += group.length - 1; saved++; tick();
      await pause(700);
    }
  }
  return { saved, failed };
}
